import { useEffect, useRef } from 'react'
import { callAPI } from '../hooks/api'
import { useState } from 'react'
import {
  MapProvider,
  Map,
  useMap,
  Source,
  Layer,
  Popup,
  Marker,
} from 'react-map-gl/maplibre'
import Spinner from './Spinner'
import { isMobile } from 'react-device-detect'

export default function FloodData({ wkt, apn, lat, lon, localAuth, setFloodData }) {
  const [loading, setLoading] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [floodGeoJson, setFloodGeoJson] = useState({ features: [] })
  const mapRef = useRef(0)
  const layerStyle = {
    id: 'Google Satellite Hybrid',
    type: 'raster',
    source: 'google_earth_hybrid',
    layout: { visibility: 'visible' },
  }

  const [showPopup, setShowPopup] = useState(false)
  const [popupLat, setPopupLat] = useState(0)
  const [popupLon, setPopupLon] = useState(0)
  const [popupText, setPopupText] = useState('')

  async function fetchFloodData() {
    var endpoint = '/get_flood_from_wkt'
    var params = {
      wkt: wkt,
      session_key: localAuth.sessionKey,
    }
    setLoading(true)
    callAPI(endpoint, params).then((res) => {
      if (res.status === 'success') {
        setFloodGeoJson(res.flood)
        setFloodData(res.flood)
      } 
      setLoading(false)
    })
  }

  async function onLoad() {
    setMapLoaded(true)
  }

  useEffect(() => {
    // Test code to invoke the update
    if (mapLoaded && floodGeoJson.features.length > 0) {
      floodGeoJson.features.map((feature) => {
        mapRef.current.on(
          'mousemove',
          `property-layer-${feature.id}`,
          function (e) {
            var props_properties =
              `
                            <div style="width: 500px;">
                                <div key="FLD_ZONE-${feature.id}" style="font-size:16px">Symbology : <strong>` +
              feature.properties.fld_zone +
              `</strong><br></div>
                                <div key="ZONE_SUBTY-${feature.id}" style="font-size:16px">Flood Zone Subtype : <strong>` +
              feature.properties.zone_subty +
              `</strong><br></div>
                                <div key="DESC-${feature.id}" style="font-size:16px">Zone Description : <strong>` +
              feature.properties.DESC +
              `</strong><br></div>
                            </div>
                            `
            var coordinates = e.lngLat
            setPopupLat(coordinates.lat)
            setPopupLon(coordinates.lng)
            setPopupText(props_properties)
            setShowPopup(true)
          }
        )
        mapRef.current.on(
          'click',
          `property-layer-${feature.id}`,
          function (e) {
            setShowPopup(false)
          }
        )
        mapRef.current.on(
          'mouseleave',
          `property-layer-${feature.id}`,
          function (e) {
            setShowPopup(false)
          }
        )
      })
    }
  }, [mapLoaded, floodGeoJson])

  useEffect(() => {
    if (floodGeoJson.features.length === 0) fetchFloodData()
  }, [])

  function floodMapHex(string) {
    if (string === '0.2% Annual Chance Flood Hazard') {
      return '#9D7DC0'
    } else if (string === '1% Annual Chance Flood Hazard') {
      return '#8E3494'
    } else if (string === 'Future Conditions 1% Annual Chance Flood Hazard') {
      return '#F5C8B3'
    } else if (string === 'Regulatory Floodway') {
      return '#769CCD'
    } else if (string === 'Area with Reduced Risk Due to Levee') {
      return '#E5D099'
    } else if (string === 'Special Floodway') {
      return '#81C0C9'
    } else {
      return 'FFA500'
    }
  }

  return (
    <>
      {loading && <Spinner local={true} />}
      <a
        className="font-bold ml-[30px] flex items-center justify-between text-[#457b9d] text-m font-medium"
        href={`https://msc.fema.gov/portal/firmette?latitude=${lat}&longitude=${lon}`}
        target="_blank"
      >
        Flood Map Download
      </a>
      {floodGeoJson && floodGeoJson.features.length > 0 && (
        <>
          <p className="ml-[30px] ">
            Zone description will display on map hover
          </p>

          <div className="w-full h-[200px] md:h-[400px] z-10 overflow-x-hidden mt-6 md:mt-0 md:ml-2">
            <MapProvider>
              <Map
                preserveDrawingBuffer={true}
                ref={mapRef}
                id="floodrisk-view"
                onLoad={onLoad}
                initialViewState={{
                  longitude: lon,
                  latitude: lat,
                  zoom: isMobile ? 10 : 11,
                }}
                style={{ height: '100%', width: '100%' }}
                // preserveDrawingBuffer={true}
              >
                <Source
                  id="google_earth_hybrid"
                  type="raster"
                  tiles={['https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}']}
                  tileSize={256}
                >
                  <Layer {...layerStyle} />
                </Source>
                {showPopup && (
                  <Popup
                    closeButton={false}
                    longitude={popupLon}
                    latitude={popupLat}
                    style={{ maxWidth: '600px' }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: popupText }} />
                  </Popup>
                )}
                {floodGeoJson.features.map((feature) => (
                  <Source
                    key={`property-source-${feature.id}`}
                    id={`property-source-${feature.id}`}
                    type="geojson"
                    data={feature}
                  >
                    <Layer
                      {...{
                        source: `property-source-${feature.id}`,
                        id: `property-layer-${feature.id}`,
                        type: 'fill',
                        layout: { visibility: 'visible' },
                        paint: {
                          'fill-color': feature.properties.hex,
                          'fill-opacity': 0.2,
                          'fill-outline-color': feature.properties.hex,
                        },
                      }}
                    />
                    <Layer
                      {...{
                        source: `property-source-${feature.id}`,
                        id: `property-layer-outline-${feature.id}`,
                        type: 'line',
                        layout: {
                          visibility: 'visible',
                          'line-join': 'round',
                          'line-cap': 'round',
                        },
                        paint: {
                          'line-color': feature.properties.hex,
                          'line-width': 4,
                        },
                      }}
                    />
                  </Source>
                ))}
                <Marker longitude={lon} latitude={lat} color="red"></Marker>
              </Map>
            </MapProvider>
          </div>
        </>
      )}
    </>
  )
}
